import {
  action,
  computed,
  makeAutoObservable,
  observable,
  runInAction,
} from "mobx"

import APIOnboarding from "@/api/onboarding"
import {
  IImmersiveGuideDetail,
  IReturnReasonLocales,
  Locale,
} from "@/api/onboarding/type"
import {
  computeRegionDefaultDays,
  getCategoryOptions,
  getSolutionOptions,
  OnboardingSolutionOption,
} from "@/init/Entry/Onboading/config"
import stores from "@/stores"

import UserInfoStoreClass from "../UserInfoStore"
import { OnboardingCategory, OnboardingReasonItem } from "./type"

const getUserInfoStore = () => stores.UserInfoStore || UserInfoStoreClass

const SUCCESS_CODE = 1000

const clampStep = (value: number): 1 | 2 | 3 | 4 => {
  if (value <= 1) return 1
  if (value >= 4) return 4
  return Math.round(value) as 1 | 2 | 3 | 4
}

const isValidLocale = (code: string): code is Locale =>
  [
    "cs",
    "da",
    "de",
    "en",
    "es",
    "fi",
    "fr",
    "hr",
    "it",
    "ja",
    "nb",
    "nl",
    "pl",
    "pt-BR",
    "pt-PT",
    "sk",
    "sl",
    "sv",
    "zh-CN",
  ].includes(code)

const resolveLocale = (raw?: string): Locale => {
  if (raw && isValidLocale(raw)) return raw
  return "en"
}

const defaultData: IImmersiveGuideDetail = {
  current_step: 1,
  status: 0,
  step_1: { region: [], types: [] },
  step_2: {
    reasons: [],
    return_window: { days: 30, solutions: [], type: "1" },
  },
  step_3: { color: "", logo: "" },
}

const EMPTY_STEP_2: IImmersiveGuideDetail["step_2"] = {
  reasons: [],
  return_window: { days: 30, solutions: [], type: "1" },
}

const DEFAULT_SOLUTION_VALUES = [5, 3, 1] as const

const findSolutionByValue = (
  value: number,
): OnboardingSolutionOption | undefined =>
  getSolutionOptions().find((opt) => opt.value === value)

const parseServerSolutions = (
  raw: number[] | undefined,
): OnboardingSolutionOption[] => {
  if (!raw?.length) return []
  const result: OnboardingSolutionOption[] = []
  const seen = new Set<number>()
  for (const value of raw) {
    if (!Number.isFinite(value) || seen.has(value)) continue
    const opt = findSolutionByValue(value)
    if (opt) {
      seen.add(value)
      result.push(opt)
    }
  }
  return result
}

const computeDefaultSolutions = (
  isOpenStoreCredit: boolean,
  isCreatedGiftCard: boolean,
): OnboardingSolutionOption[] => {
  const defaults: OnboardingSolutionOption[] = DEFAULT_SOLUTION_VALUES.map(
    (value) => findSolutionByValue(value),
  ).filter((opt): opt is OnboardingSolutionOption => Boolean(opt))
  if (isOpenStoreCredit) {
    const opt = findSolutionByValue(7)
    if (opt) defaults.push(opt)
  }
  if (isCreatedGiftCard) {
    const giftCard = findSolutionByValue(4)
    const discount = findSolutionByValue(2)
    if (giftCard) defaults.push(giftCard)
    if (discount) defaults.push(discount)
  }
  return defaults
}

export default class OnboardingStore {
  @observable currentStep: 1 | 2 | 3 | 4 = 1
  @observable data: IImmersiveGuideDetail = defaultData
  @observable reasonsLocales: IReturnReasonLocales = {}

  @observable selectedCategories: OnboardingCategory[] = ["apparel_accessories"]
  @observable selectedRegions: string[] = ["us"]
  @observable selectedReasons: number[] = []
  @observable selectedSolutions: OnboardingSolutionOption[] = []
  @observable returnDays: number = 30
  @observable returnRuleType: string = "order_fulfillment"
  @observable themeColor: string = "#1274FF"
  @observable logoUrl: string | undefined = undefined

  @observable isSaving: boolean = false
  @observable isLoading: boolean = false
  @observable isInitialized: boolean = false
  @observable expandedCards: Set<number> = new Set([1])
  @observable isOpenStoreCredit: boolean = false
  @observable isCreatedGiftCard: boolean = false

  private initializePromise: Promise<void> | null = null

  constructor() {
    makeAutoObservable(this)
  }

  @computed
  get currentLocale(): Locale {
    return resolveLocale(getUserInfoStore().data?.language_admin)
  }

  @computed
  get currentReasons(): OnboardingReasonItem[] {
    return (
      this.unionReasons(this.reasonsLocales[this.currentLocale]) ||
      (this.currentLocale !== "en"
        ? this.unionReasons(this.reasonsLocales.en)
        : [])
    )
  }

  private unionReasons(
    bucket?: IReturnReasonLocales[Locale],
  ): OnboardingReasonItem[] {
    if (!bucket) return []
    const seen = new Set<number>()
    const result: OnboardingReasonItem[] = []
    for (const { value: category } of getCategoryOptions()) {
      if (!this.selectedCategories.includes(category)) continue
      const reasons =
        bucket.find((item) => item.type === category)?.reasons ?? []
      for (const reason of reasons) {
        if (seen.has(reason.id)) continue
        seen.add(reason.id)
        result.push({ ...reason, category })
      }
    }
    return result
  }

  @action.bound
  setCurrentStep(step: number) {
    this.currentStep = clampStep(step)
  }

  @action.bound
  setSelectedCategories(values: OnboardingCategory[]) {
    this.selectedCategories = values
    this.resetSelectedReasonsToCurrent()
  }

  @action.bound
  toggleCategory(value: OnboardingCategory) {
    this.selectedCategories = this.selectedCategories.includes(value)
      ? this.selectedCategories.filter((item) => item !== value)
      : [...this.selectedCategories, value]
    this.resetSelectedReasonsToCurrent()
  }

  @action.bound
  private resetSelectedReasonsToCurrent() {
    this.selectedReasons = this.currentReasons.map((r) => r.id)
  }

  @action.bound
  setSelectedRegions(values: string[]) {
    this.selectedRegions = values
    this.syncReturnDaysFromRegions()
  }

  @action.bound
  toggleRegion(value: string) {
    this.selectedRegions = this.selectedRegions.includes(value)
      ? this.selectedRegions.filter((item) => item !== value)
      : [...this.selectedRegions, value]
    this.syncReturnDaysFromRegions()
  }

  @action.bound
  private syncReturnDaysFromRegions() {
    this.returnDays = computeRegionDefaultDays(this.selectedRegions)
  }

  @action.bound
  setSelectedReasons(values: number[]) {
    this.selectedReasons = values
  }

  @action.bound
  toggleReason(reasonId: number) {
    this.selectedReasons = this.selectedReasons.includes(reasonId)
      ? this.selectedReasons.filter((id) => id !== reasonId)
      : [...this.selectedReasons, reasonId]
  }

  @action.bound
  setSelectedSolutions(values: OnboardingSolutionOption[]) {
    this.selectedSolutions = values
  }

  @action.bound
  toggleSolution(value: number) {
    const exists = this.selectedSolutions.some((item) => item.value === value)
    if (exists) {
      this.selectedSolutions = this.selectedSolutions.filter(
        (item) => item.value !== value,
      )
    } else {
      const opt = findSolutionByValue(value)
      if (opt) this.selectedSolutions = [...this.selectedSolutions, opt]
    }
  }

  @action.bound
  setReturnDays(days: number) {
    this.returnDays = Math.min(365, Math.max(0, Math.floor(days)))
  }

  @action.bound
  setReturnRuleType(type: string) {
    this.returnRuleType = type
  }

  @action.bound
  setThemeColor(color: string) {
    this.themeColor = color
  }

  @action.bound
  setLogoUrl(url: string | undefined) {
    this.logoUrl = url
  }

  @action.bound
  toggleExpandedCard(id: number) {
    const next = new Set(this.expandedCards)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    this.expandedCards = next
  }

  @action.bound
  private applyServerData(detail: IImmersiveGuideDetail) {
    this.data = { ...defaultData, ...detail }
    this.currentStep = clampStep(detail.current_step || 1)

    const step1 = detail.step_1 || { region: [], types: [] }
    this.selectedCategories = (
      step1.types?.length ? step1.types : ["apparel_accessories"]
    ) as OnboardingCategory[]
    this.selectedRegions = step1.region?.length ? step1.region : ["us"]

    const step2 = detail.step_2 || EMPTY_STEP_2
    this.selectedReasons = step2.reasons ? [...step2.reasons] : []
    if (!this.selectedReasons.length && this.currentReasons.length) {
      this.selectedReasons = this.currentReasons.map((r) => r.id)
    }
    this.returnDays = step2.return_window?.days || 30
    this.returnRuleType = step2.return_window?.type || "order_fulfillment"
    this.selectedSolutions = step2.return_window?.solutions?.length
      ? parseServerSolutions(step2.return_window.solutions)
      : computeDefaultSolutions(this.isOpenStoreCredit, this.isCreatedGiftCard)

    const step3 = detail.step_3 || { color: "", logo: "" }
    this.themeColor = step3.color || "#1274FF"
    this.logoUrl = step3.logo || undefined
  }

  @action.bound
  async initialize() {
    if (this.isInitialized) return
    if (this.initializePromise) return this.initializePromise

    this.isLoading = true
    this.initializePromise = (async () => {
      try {
        const detailRes = await APIOnboarding.getImmersiveGuideDetail()
        this.isLoading = false
        const [localesRes, flagsRes] = await Promise.all([
          APIOnboarding.getReasonsLocales(),
          APIOnboarding.getShopifyFlags(),
        ])

        runInAction(() => {
          if (localesRes?.data?.data) {
            this.reasonsLocales = localesRes.data.data
          }
          if (flagsRes?.code === SUCCESS_CODE && flagsRes.data) {
            this.isOpenStoreCredit = !!flagsRes.data.is_open_store_credit
            this.isCreatedGiftCard = !!flagsRes.data.is_created_gift_card
          }
          if (detailRes?.code === SUCCESS_CODE && detailRes.data) {
            this.applyServerData(detailRes.data)
          }
          this.isInitialized = true
        })
      } finally {
        runInAction(() => {
          this.isLoading = false
          this.initializePromise = null
        })
      }
    })()

    return this.initializePromise
  }

  @action.bound
  private buildPayload(): Omit<
    IImmersiveGuideDetail,
    "isCreatedGiftCard" | "isOpenStoreCredit" | "status"
  > {
    return {
      current_step: this.currentStep,
      step_1: {
        region: [...this.selectedRegions],
        types: [...this.selectedCategories],
      },
      step_2: {
        reasons: [...this.selectedReasons],
        return_window: {
          days: this.returnDays,
          solutions: this.selectedSolutions.map((item) => item.value),
          type: this.returnRuleType,
        },
      },
      step_3: {
        color: this.themeColor,
        logo: this.logoUrl || "",
      },
    }
  }

  @action.bound
  async saveStep1() {
    this.isSaving = true
    try {
      const payload: Omit<
        IImmersiveGuideDetail,
        "isCreatedGiftCard" | "isOpenStoreCredit" | "status"
      > = {
        current_step: 2,
        step_1: {
          region: [...this.selectedRegions],
          types: [...this.selectedCategories],
        },
        step_2: this.data.step_2,
        step_3: this.data.step_3,
      }
      const res = await APIOnboarding.updateImmersiveGuide(payload)
      if (res?.code === SUCCESS_CODE) {
        runInAction(() => {
          this.data = {
            ...this.data,
            ...payload,
          }
          this.currentStep = 2
        })
        return true
      }
      return false
    } finally {
      runInAction(() => {
        this.isSaving = false
      })
    }
  }

  @action.bound
  async saveStep2() {
    this.isSaving = true
    try {
      const payload = {
        ...this.buildPayload(),
        current_step: 3 as const,
      }
      const res = await APIOnboarding.updateImmersiveGuide(payload)
      if (res?.code === SUCCESS_CODE) {
        runInAction(() => {
          this.data = { ...this.data, ...payload }
          this.currentStep = 3
        })
        return true
      }
      return false
    } finally {
      runInAction(() => {
        this.isSaving = false
      })
    }
  }

  @action.bound
  async saveStep3() {
    this.isSaving = true
    try {
      const payload = {
        ...this.buildPayload(),
        current_step: 4 as const,
      }
      const res = await APIOnboarding.updateImmersiveGuide(payload)
      if (res?.code === SUCCESS_CODE) {
        runInAction(() => {
          this.data = { ...this.data, ...payload }
          this.currentStep = 4
        })
        return true
      }
      return false
    } finally {
      runInAction(() => {
        this.isSaving = false
      })
    }
  }

  @action.bound
  async goBack(step: number) {
    const targetStep = clampStep(step)
    if (targetStep < 1 || targetStep >= this.currentStep) return false
    this.isSaving = true
    try {
      const payload = {
        ...this.buildPayload(),
        current_step: targetStep,
      }
      APIOnboarding.updateImmersiveGuide(payload)
      runInAction(() => {
        this.data = { ...this.data, ...payload }
        this.currentStep = targetStep
      })
      return true
    } finally {
      runInAction(() => {
        this.isSaving = false
      })
    }
  }

  @action.bound
  async finish() {
    this.isSaving = true
    try {
      const res = await APIOnboarding.completeImmersiveGuide()
      if (res?.code === SUCCESS_CODE) {
        return true
      }
      return false
    } finally {
      runInAction(() => {
        this.isSaving = false
      })
    }
  }

  @action.bound
  async skip() {
    this.isSaving = true
    try {
      const res = await APIOnboarding.skipImmersiveGuide()
      if (res?.code === SUCCESS_CODE) {
        return true
      }
      return false
    } finally {
      runInAction(() => {
        this.isSaving = false
      })
    }
  }

  @action.bound
  reset() {
    this.currentStep = 1
    this.data = defaultData
    this.reasonsLocales = {}
    this.selectedCategories = ["apparel_accessories"]
    this.selectedRegions = ["us"]
    this.selectedReasons = []
    this.selectedSolutions = []
    this.returnDays = 30
    this.returnRuleType = "order_fulfillment"
    this.themeColor = "#1274FF"
    this.logoUrl = undefined
    this.isSaving = false
    this.isLoading = false
    this.isInitialized = false
    this.isOpenStoreCredit = false
    this.isCreatedGiftCard = false
    this.expandedCards = new Set([1])
    this.initializePromise = null
  }
}
